import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, ArrowLeft, AlertCircle } from 'lucide-react';
import useAssessmentStore from '../hooks/useAssessment';

const personalEmailDomains = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com', 
  'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com',
  'yandex.com', 'gmx.com', 'fastmail.com', 'tutanota.com', 'hey.com',
  'pm.me', 'proton.me', 'mailinator.com', 'guerrillamail.com', 'tempmail.com',
  'throwaway.email', '10minutemail.com', 'fakeinbox.com', 'sharklasers.com',
  'guerrillamail.info', 'grr.la', 'dispostable.com', 'mailnesia.com',
  'maildrop.cc', 'getnada.com', 'temp-mail.org', 'emailondeck.com',
  'yahoo.co.uk', 'yahoo.co.in', 'hotmail.co.uk', 'googlemail.com',
  'msn.com', 'att.net', 'sbcglobal.net', 'bellsouth.net', 'comcast.net',
  'verizon.net', 'cox.net', 'earthlink.net', 'juno.com', 'netzero.net',
  'me.com', 'mac.com', 'rediffmail.com', 'web.de', 'gmx.de', 'mail.ru',
  'ymail.com', 'rocketmail.com'
];

const industries = [
  { value: 'saas', label: 'SaaS / Software' },
  { value: 'fintech', label: 'FinTech / Financial Services' },
  { value: 'healthcare', label: 'Healthcare / HealthTech' },
  { value: 'ecommerce', label: 'E-commerce / Retail' },
  { value: 'manufacturing', label: 'Manufacturing / Industrial' },
  { value: 'professional_services', label: 'Professional Services / Consulting' },
  { value: 'logistics', label: 'Logistics / Supply Chain' },
  { value: 'real_estate', label: 'Real Estate / PropTech' },
  { value: 'media', label: 'Media / Entertainment' },
  { value: 'other', label: 'Other' }
];

const companySizes = [
  { value: '1-50', label: '1-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1000 employees' },
  { value: '1000+', label: '1000+ employees' }
];

const schema = z.object({
  name: z.string().min(2, 'Please enter your name'),
  email: z.string()
    .email('Please enter a valid email')
    .refine((email) => {
      const domain = email.split('@')[1]?.toLowerCase();
      return domain && !personalEmailDomains.includes(domain);
    }, 'Please use your work email address. Personal emails (Gmail, Yahoo, etc.) are not accepted.'),
  company: z.string().min(2, 'Please enter your company name'),
  industry: z.string().min(1, 'Please select your industry'),
  companySize: z.string().min(1, 'Please select your company size'),
  role: z.string().optional()
});

const EmailGate = () => {
  const navigate = useNavigate();
  const setUserData = useAssessmentStore((state) => state.setUserData);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      industry: '',
      companySize: '',
      role: ''
    }
  });

  const onSubmit = (data) => {
    setUserData(data);
    navigate('/assess/questions');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-950 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
          <span className="font-serif text-xl tracking-tight">Unpaved</span>
        </div>
      </header>

      {/* Form Section */}
      <main className="flex-1 flex items-center justify-center px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full max-w-md"
        >
          <motion.h2
            variants={itemVariants}
            className="font-serif text-3xl sm:text-4xl tracking-tight mb-4"
          >
            Before we begin
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-zinc-600 leading-relaxed mb-10"
          >
            We'll send your full score report and personalised recommendations to your inbox.
          </motion.p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Name Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs font-semibold tracking-[0.1em] uppercase text-zinc-400 mb-2">
                Full Name
              </label>
              <input
                type="text"
                {...register('name')}
                data-testid="name-input"
                placeholder="Jane Smith"
                className="w-full border-b border-zinc-200 bg-transparent py-3 text-lg focus:border-zinc-950 focus:outline-none transition-colors placeholder:text-zinc-300"
              />
              {errors.name && (
                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.name.message}
                </p>
              )}
            </motion.div>

            {/* Email Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs font-semibold tracking-[0.1em] uppercase text-zinc-400 mb-2">
                Work Email
              </label>
              <input
                type="email"
                {...register('email')}
                data-testid="email-input"
                placeholder="jane@company.com"
                className="w-full border-b border-zinc-200 bg-transparent py-3 text-lg focus:border-zinc-950 focus:outline-none transition-colors placeholder:text-zinc-300"
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.email.message}
                </p>
              )}
            </motion.div>

            {/* Company Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs font-semibold tracking-[0.1em] uppercase text-zinc-400 mb-2">
                Company Name
              </label>
              <input
                type="text"
                {...register('company')}
                data-testid="company-input"
                placeholder="Acme Corp"
                className="w-full border-b border-zinc-200 bg-transparent py-3 text-lg focus:border-zinc-950 focus:outline-none transition-colors placeholder:text-zinc-300"
              />
              {errors.company && (
                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.company.message}
                </p>
              )}
            </motion.div>

            {/* Industry Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs font-semibold tracking-[0.1em] uppercase text-zinc-400 mb-2">
                Industry
              </label>
              <select
                {...register('industry')}
                data-testid="industry-input"
                className="w-full border-b border-zinc-200 bg-transparent py-3 text-lg focus:border-zinc-950 focus:outline-none transition-colors text-zinc-900 appearance-none cursor-pointer"
              >
                <option value="" className="text-zinc-300">Select your industry</option>
                {industries.map(ind => (
                  <option key={ind.value} value={ind.value}>{ind.label}</option>
                ))}
              </select>
              {errors.industry && (
                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.industry.message}
                </p>
              )}
            </motion.div>

            {/* Company Size Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs font-semibold tracking-[0.1em] uppercase text-zinc-400 mb-2">
                Company Size
              </label>
              <select
                {...register('companySize')}
                data-testid="company-size-input"
                className="w-full border-b border-zinc-200 bg-transparent py-3 text-lg focus:border-zinc-950 focus:outline-none transition-colors text-zinc-900 appearance-none cursor-pointer"
              >
                <option value="" className="text-zinc-300">Select company size</option>
                {companySizes.map(size => (
                  <option key={size.value} value={size.value}>{size.label}</option>
                ))}
              </select>
              {errors.companySize && (
                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.companySize.message}
                </p>
              )}
            </motion.div>

            {/* Role Field (Optional) */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs font-semibold tracking-[0.1em] uppercase text-zinc-400 mb-2">
                Your Role <span className="text-zinc-300">(Optional)</span>
              </label>
              <input
                type="text"
                {...register('role')}
                data-testid="role-input"
                placeholder="e.g. COO, Ops Director"
                className="w-full border-b border-zinc-200 bg-transparent py-3 text-lg focus:border-zinc-950 focus:outline-none transition-colors placeholder:text-zinc-300"
              />
            </motion.div>

            {/* Submit Button */}
            <motion.div variants={itemVariants} className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="begin-assessment-btn"
                className="w-full flex items-center justify-center gap-2 bg-zinc-950 text-white px-8 py-4 text-sm font-medium hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50 btn-glow"
              >
                Begin Assessment
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>

            {/* Privacy Note */}
            <motion.p
              variants={itemVariants}
              className="text-xs text-zinc-400 text-center"
            >
              No spam. Your data is used only to generate your report.
            </motion.p>
          </form>
        </motion.div>
      </main>
    </div>
  );
};

export default EmailGate;
